/**
 * Exchanges Tab Component
 *
 * Allows users to manage their exchange API credentials (Binance, etc.)
 */

import { useState } from 'react';
import { Icons } from '../Icons';
import { AIConsensusAPI, type ExchangeCredentials } from '../../services/aiConsensusAPI';
import { useToast } from '../Toast';
import { useConfirmation } from '../../hooks/useConfirmation';
import { ConfirmationModal } from '../ui/ConfirmationModal';

interface ExchangesTabProps {
  exchanges: ExchangeCredentials[];
  isAuthenticated: boolean;
  onShowAuthModal: () => void;
  onRefresh: () => Promise<void>;
}

export function ExchangesTab({ exchanges, isAuthenticated, onShowAuthModal, onRefresh }: ExchangesTabProps) {
  const toast = useToast();
  const { confirm, confirmationProps } = useConfirmation();
  const [showAddModal, setShowAddModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [balances, setBalances] = useState<Record<string, { balances: Record<string, { free: number; locked: number; total: number }>; totalValueUsdt: number }>>({});
  const [loadingBalances, setLoadingBalances] = useState<Record<string, boolean>>({});

  // Add exchange form state
  const [formData, setFormData] = useState({
    exchange: 'binance' as 'binance' | 'cryptocom',
    apiKey: '',
    apiSecret: '',
    label: '',
    testnet: false,
  });
  const [formError, setFormError] = useState<string | null>(null);

  const handleAddExchange = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setIsLoading(true);

    try {
      await AIConsensusAPI.connectExchange({
        exchange: formData.exchange,
        apiKey: formData.apiKey,
        apiSecret: formData.apiSecret,
        label: formData.label || undefined,
        testnet: formData.testnet,
      });

      // Reset form and close modal
      setFormData({ exchange: 'binance', apiKey: '', apiSecret: '', label: '', testnet: false });
      setShowAddModal(false);
      await onRefresh();
    } catch (err: any) {
      setFormError(err.message || 'Erreur lors de la connexion');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestConnection = async (id: string) => {
    setTestingId(id);
    try {
      const success = await AIConsensusAPI.testExchange(id);
      if (success) {
        toast.success('Connexion réussie !');
      } else {
        toast.error('Échec de la connexion. Vérifiez vos clés API.');
      }
    } catch {
      toast.error('Erreur lors du test de connexion');
    } finally {
      setTestingId(null);
    }
  };

  const handleGetBalance = async (id: string) => {
    setLoadingBalances(prev => ({ ...prev, [id]: true }));
    try {
      const balance = await AIConsensusAPI.getExchangeBalance(id);
      if (balance) {
        setBalances(prev => ({ ...prev, [id]: balance }));
      }
    } catch {
      toast.error('Erreur lors de la récupération du solde');
    } finally {
      setLoadingBalances(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleDisconnect = async (id: string) => {
    const confirmed = await confirm({
      title: 'Déconnecter l\'exchange',
      message: 'Êtes-vous sûr de vouloir déconnecter cet exchange ?',
      confirmText: 'Déconnecter',
      cancelText: 'Annuler',
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await AIConsensusAPI.disconnectExchange(id);
      await onRefresh();
      setBalances(prev => {
        const newBalances = { ...prev };
        delete newBalances[id];
        return newBalances;
      });
    } catch {
      toast.error('Erreur lors de la déconnexion');
    }
  };

  // If not authenticated, show login prompt
  if (!isAuthenticated) {
    return (
      <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
        <Icons.Lock size={48} style={{ opacity: 0.3, marginBottom: '16px', color: 'var(--muted)' }} />
        <h3 style={{ marginBottom: '8px' }}>Connexion requise</h3>
        <p style={{ color: 'var(--muted)', marginBottom: '24px' }}>
          Connectez-vous pour gerer vos cles API exchange de maniere securisee.
        </p>
        <button
          onClick={onShowAuthModal}
          style={{
            padding: '12px 24px',
            background: 'var(--primary)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Icons.LogIn size={18} />
          Se connecter
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icons.Wallet size={24} />
            Mes Exchanges
          </h2>
          <p style={{ margin: '8px 0 0', color: 'var(--muted)', fontSize: '14px' }}>
            Gerez vos connexions Binance pour le trading automatise
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          style={{
            padding: '10px 16px',
            background: 'var(--primary)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Icons.Plus size={18} />
          Ajouter un exchange
        </button>
      </div>

      {/* Security Notice */}
      <div style={{
        padding: '16px',
        background: 'rgba(139, 92, 246, 0.1)',
        border: '1px solid rgba(139, 92, 246, 0.3)',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px'
      }}>
        <Icons.Shield size={20} style={{ color: '#8B5CF6', flexShrink: 0, marginTop: '2px' }} />
        <div style={{ fontSize: '13px', color: 'var(--text)' }}>
          <strong style={{ color: '#8B5CF6' }}>Securite</strong>
          <p style={{ margin: '4px 0 0', color: 'var(--muted)' }}>
            Vos cles API sont chiffrees avec AES-256-GCM et stockees de maniere securisee.
            Utilisez des cles avec permissions de trading uniquement (pas de retrait).
          </p>
        </div>
      </div>

      {/* Exchanges List */}
      {exchanges.length === 0 ? (
        <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
          <Icons.Wallet size={40} style={{ opacity: 0.3, marginBottom: '12px' }} />
          <h3 style={{ marginBottom: '8px', fontSize: '16px' }}>Aucun exchange connecte</h3>
          <p style={{ color: 'var(--muted)', fontSize: '14px', marginBottom: '20px' }}>
            Ajoutez vos cles API Binance pour commencer le trading automatise
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              padding: '10px 20px',
              background: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Icons.Plus size={16} />
            Connecter Binance
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {exchanges.map((ex) => (
            <div key={ex.id} className="card" style={{ padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                {/* Exchange Info */}
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: ex.exchange === 'binance' ? 'linear-gradient(135deg, #F3BA2F, #E8A900)' : 'linear-gradient(135deg, #1199FA, #0d7dd4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    fontWeight: 700,
                    color: ex.exchange === 'binance' ? '#1E2026' : 'white'
                  }}>
                    {ex.exchange === 'binance' ? 'B' : 'C'}
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '16px', textTransform: 'capitalize' }}>
                      {ex.exchange}
                      {ex.testnet && (
                        <span style={{
                          marginLeft: '8px',
                          padding: '2px 8px',
                          background: 'rgba(245, 158, 11, 0.2)',
                          color: '#F59E0B',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 500
                        }}>
                          TESTNET
                        </span>
                      )}
                    </h3>
                    {ex.label && (
                      <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: '13px' }}>
                        {ex.label}
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <span style={{
                        padding: '3px 8px',
                        background: ex.isConnected ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                        color: ex.isConnected ? '#22C55E' : '#EF4444',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 500
                      }}>
                        {ex.isConnected ? 'Connecte' : 'Deconnecte'}
                      </span>
                      <span style={{
                        padding: '3px 8px',
                        background: 'var(--bg)',
                        color: 'var(--muted)',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        API: ...{ex.apiKey?.slice(-8)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleGetBalance(ex.id)}
                    disabled={loadingBalances[ex.id]}
                    style={{
                      padding: '8px 12px',
                      background: 'var(--bg)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      cursor: loadingBalances[ex.id] ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                      color: 'var(--text)'
                    }}
                  >
                    {loadingBalances[ex.id] ? (
                      <Icons.Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <Icons.DollarSign size={14} />
                    )}
                    Solde
                  </button>
                  <button
                    onClick={() => handleTestConnection(ex.id)}
                    disabled={testingId === ex.id}
                    style={{
                      padding: '8px 12px',
                      background: 'var(--bg)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      cursor: testingId === ex.id ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                      color: 'var(--text)'
                    }}
                  >
                    {testingId === ex.id ? (
                      <Icons.Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <Icons.Zap size={14} />
                    )}
                    Tester
                  </button>
                  <button
                    onClick={() => handleDisconnect(ex.id)}
                    style={{
                      padding: '8px 12px',
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                      color: '#EF4444'
                    }}
                  >
                    <Icons.Trash2 size={14} />
                    Supprimer
                  </button>
                </div>
              </div>

              {/* Balance Display */}
              {balances[ex.id] && (
                <div style={{
                  marginTop: '16px',
                  padding: '16px',
                  background: 'var(--bg)',
                  borderRadius: '8px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}>
                    <span style={{ fontSize: '14px', fontWeight: 600 }}>Solde Total</span>
                    <span style={{ fontSize: '18px', fontWeight: 700, color: '#22C55E' }}>
                      ${balances[ex.id].totalValueUsdt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px' }}>
                    {Object.entries(balances[ex.id].balances)
                      .filter(([_, bal]) => bal.total > 0)
                      .slice(0, 8)
                      .map(([asset, bal]) => (
                        <div key={asset} style={{
                          padding: '8px',
                          background: 'var(--card-bg)',
                          borderRadius: '6px',
                          fontSize: '12px'
                        }}>
                          <div style={{ fontWeight: 600, marginBottom: '2px' }}>{asset}</div>
                          <div style={{ color: 'var(--muted)' }}>
                            {bal.total.toLocaleString(undefined, { maximumFractionDigits: 6 })}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Exchange Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '480px',
            background: 'var(--card-bg)',
            border: '1px solid var(--border)',
            borderRadius: '16px',
            padding: '28px',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.2)'
          }}>
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Icons.Plus size={20} />
                Connecter un Exchange
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--muted)',
                  padding: '4px'
                }}
              >
                <Icons.X size={20} />
              </button>
            </div>

            {/* Error */}
            {formError && (
              <div style={{
                padding: '12px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#EF4444',
                fontSize: '13px'
              }}>
                <Icons.AlertCircle size={16} />
                {formError}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleAddExchange}>
              {/* Exchange Select */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                  Exchange
                </label>
                <select
                  aria-label="Exchange"
                  value={formData.exchange}
                  onChange={(e) => setFormData(prev => ({ ...prev, exchange: e.target.value as 'binance' | 'cryptocom' }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'var(--bg)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: 'var(--text)'
                  }}
                >
                  <option value="binance">Binance</option>
                  <option value="cryptocom" disabled>Crypto.com (bientot)</option>
                </select>
              </div>

              {/* Label */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                  Label <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(optionnel)</span>
                </label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="Ex: Mon compte principal"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'var(--bg)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: 'var(--text)'
                  }}
                />
              </div>

              {/* API Key */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                  API Key
                </label>
                <input
                  type="text"
                  value={formData.apiKey}
                  onChange={(e) => setFormData(prev => ({ ...prev, apiKey: e.target.value }))}
                  placeholder="Votre cle API Binance"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'var(--bg)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: 'var(--text)',
                    fontFamily: 'monospace'
                  }}
                />
              </div>

              {/* API Secret */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                  API Secret
                </label>
                <input
                  type="password"
                  value={formData.apiSecret}
                  onChange={(e) => setFormData(prev => ({ ...prev, apiSecret: e.target.value }))}
                  placeholder="Votre secret API Binance"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'var(--bg)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: 'var(--text)',
                    fontFamily: 'monospace'
                  }}
                />
              </div>

              {/* Testnet Toggle */}
              <div style={{
                marginBottom: '20px',
                padding: '12px',
                background: 'var(--bg)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 500 }}>Mode Testnet</div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                    Utiliser les API testnet pour tester sans risque
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, testnet: !prev.testnet }))}
                  style={{
                    width: '48px',
                    height: '26px',
                    borderRadius: '13px',
                    border: 'none',
                    background: formData.testnet ? '#22C55E' : 'var(--border)',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'background 0.2s'
                  }}
                >
                  <div style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    background: 'white',
                    position: 'absolute',
                    top: '2px',
                    left: formData.testnet ? '24px' : '2px',
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }} />
                </button>
              </div>

              {/* Info */}
              <div style={{
                padding: '12px',
                background: 'rgba(245, 158, 11, 0.1)',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '12px',
                color: 'var(--muted)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', color: '#F59E0B', fontWeight: 500 }}>
                  <Icons.AlertTriangle size={14} />
                  Permissions recommandees
                </div>
                <p style={{ margin: 0 }}>
                  Activez uniquement : <strong>Enable Reading</strong>, <strong>Enable Spot Trading</strong>.
                  Ne jamais activer les retraits.
                </p>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'var(--bg)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    color: 'var(--text)'
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !formData.apiKey || !formData.apiSecret}
                  style={{
                    flex: 2,
                    padding: '12px',
                    background: isLoading ? 'var(--muted)' : 'var(--primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {isLoading ? (
                    <>
                      <Icons.Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Connexion...
                    </>
                  ) : (
                    <>
                      <Icons.Link size={16} />
                      Connecter
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>

      <ConfirmationModal {...confirmationProps} />
    </div>
  );
}
